import React, { useState } from 'react';
import { apiClient } from '../services/api';
import './AIGenerateModal.css';

interface GeneratedScenario {
  scenario_description: string;
  expected_result?: string;
  preconditions?: string;
  priority: string;
  tags: string[];
}

interface GeneratedTestCase {
  test_case_description: string;
  preconditions?: string;
  steps?: string;
  expected_result?: string;
  priority: string;
  tags: string[];
}

interface AIGenerateModalProps {
  folderId: string;
  type: 'scenario' | 'test_case';
  onClose: () => void;
  onSaved: () => void;
}

const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#dc2626',
  High: '#f97316',
  Medium: '#3b82f6',
  Low: '#9ca3af',
};

export const AIGenerateModal: React.FC<AIGenerateModalProps> = ({
  folderId, type, onClose, onSaved
}) => {
  const [tab, setTab] = useState<'jira' | 'description'>('description');
  const [input, setInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [_sourceDescription, setSourceDescription] = useState(''); // eslint-disable-line

  // Preview state
  const [scenarios, setScenarios] = useState<GeneratedScenario[]>([]);
  const [testCases, setTestCases] = useState<GeneratedTestCase[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showPreview, setShowPreview] = useState(false);

  const label = type === 'scenario' ? 'Test Scenarios' : 'Test Cases';

  const handleGenerate = async () => {
    if (!input.trim()) {
      setError(tab === 'jira' ? 'Please enter a Jira ticket ID' : 'Please enter a description');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const resp = await apiClient.post('/api/v1/ai/generate', {
        folder_id: folderId,
        mode: tab,
        input: input.trim(),
        type,
      });
      const data = resp.data as any;
      setSourceDescription(data.source_description || input);
      if (type === 'scenario') {
        setScenarios(data.scenarios || []);
        setSelected(new Set((data.scenarios || []).map((_: any, i: number) => i)));
      } else {
        setTestCases(data.test_cases || []);
        setSelected(new Set((data.test_cases || []).map((_: any, i: number) => i)));
      }
      setShowPreview(true);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e.message || 'Generation failed';
      setError(msg);
    } finally {
      setGenerating(false);
    }
  };

  const toggleSelect = (i: number) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setSelected(next);
  };

  const toggleAll = () => {
    const total = type === 'scenario' ? scenarios.length : testCases.length;
    if (selected.size === total) setSelected(new Set());
    else setSelected(new Set(Array.from({ length: total }, (_, i) => i)));
  };

  const handleSave = async () => {
    if (selected.size === 0) { setError('Select at least one item to save'); return; }
    setSaving(true);
    setError(null);
    try {
      const body: any = { folder_id: folderId, type };
      if (type === 'scenario') {
        body.scenarios = scenarios.filter((_, i) => selected.has(i));
      } else {
        body.test_cases = testCases.filter((_, i) => selected.has(i));
      }
      await apiClient.post('/api/v1/ai/save', body);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const items = type === 'scenario' ? scenarios : testCases;
  const getTitle = (item: any) =>
    type === 'scenario' ? item.scenario_description : item.test_case_description;

  return (
    <div className="ai-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ai-header">
          <div className="ai-header-left">
            <div className="ai-header-icon">✨</div>
            <div>
              <h2 className="ai-title">Generate {label} with AI</h2>
              <p className="ai-subtitle">Powered by Google Gemini</p>
            </div>
          </div>
          <button className="ai-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {!showPreview ? (
          /* Input step */
          <div className="ai-body">
            {/* Tabs */}
            <div className="ai-tabs">
              <button
                className={`ai-tab${tab === 'description' ? ' active' : ''}`}
                onClick={() => { setTab('description'); setInput(''); setError(null); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                Enter Description
              </button>
              <button
                className={`ai-tab${tab === 'jira' ? ' active' : ''}`}
                onClick={() => { setTab('jira'); setInput(''); setError(null); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Jira Ticket
              </button>
            </div>

            {tab === 'description' ? (
              <div className="ai-field">
                <label className="ai-label">Feature / Ticket Description</label>
                <textarea
                  className="ai-textarea"
                  rows={7}
                  placeholder="Describe the feature or functionality you want to test. Be specific about user flows, edge cases, and acceptance criteria..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                />
                <p className="ai-hint">The more detail you provide, the better the generated {label.toLowerCase()} will be.</p>
              </div>
            ) : (
              <div className="ai-field">
                <label className="ai-label">Jira Ticket ID</label>
                <input
                  className="ai-input"
                  type="text"
                  placeholder="e.g. PROJ-123"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                />
                <p className="ai-hint">The ticket's summary, description, and acceptance criteria will be used as context.</p>
              </div>
            )}

            {error && <div className="ai-error">{error}</div>}

            <div className="ai-footer">
              <button className="ai-btn-cancel" onClick={onClose}>Cancel</button>
              <button className="ai-btn-generate" onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <>
                    <span className="ai-spinner" />
                    Generating...
                  </>
                ) : (
                  <>✨ Generate {label}</>
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Preview step */
          <div className="ai-body">
            <div className="ai-preview-header">
              <div>
                <p className="ai-preview-count">
                  <strong>{items.length}</strong> {label.toLowerCase()} generated
                  {' · '}<span className="ai-preview-select-all" onClick={toggleAll}>
                    {selected.size === items.length ? 'Deselect all' : 'Select all'}
                  </span>
                </p>
                <p className="ai-preview-source">
                  {tab === 'jira' ? `From Jira: ${input}` : 'From description'}
                </p>
              </div>
              <button className="ai-btn-back" onClick={() => setShowPreview(false)}>
                ← Back
              </button>
            </div>

            <div className="ai-preview-list">
              {items.map((item, i) => (
                <div
                  key={i}
                  className={`ai-preview-item${selected.has(i) ? ' selected' : ''}`}
                  onClick={() => toggleSelect(i)}
                >
                  <div className="ai-preview-check">
                    <div className={`ai-checkbox${selected.has(i) ? ' checked' : ''}`}>
                      {selected.has(i) && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="ai-preview-content">
                    <div className="ai-preview-title">{getTitle(item)}</div>
                    {item.expected_result && (
                      <div className="ai-preview-expected">{item.expected_result}</div>
                    )}
                    {type === 'test_case' && (item as GeneratedTestCase).steps && (
                      <div className="ai-preview-steps">{(item as GeneratedTestCase).steps}</div>
                    )}
                  </div>
                  <div className="ai-preview-meta">
                    <span
                      className="ai-priority-badge"
                      style={{ background: PRIORITY_COLORS[item.priority] + '20', color: PRIORITY_COLORS[item.priority] }}
                    >
                      {item.priority}
                    </span>
                    <span className="ai-tag-badge">AI Generated</span>
                  </div>
                </div>
              ))}
            </div>

            {error && <div className="ai-error">{error}</div>}

            <div className="ai-footer">
              <button className="ai-btn-cancel" onClick={onClose}>Cancel</button>
              <button
                className="ai-btn-generate"
                onClick={handleSave}
                disabled={saving || selected.size === 0}
              >
                {saving ? (
                  <><span className="ai-spinner" /> Saving...</>
                ) : (
                  <>Save {selected.size} Selected</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
